import { InlineMessage } from '@strata/react-inline-message';

import { Columns } from 'components/shared/Columns';
import { Page } from 'components/shared/Page';
import { Section } from 'components/shared/Section';

const marketChallenge = [
    <p key="1">Describes the challenges in our market and our response to those challenges.</p>,
    <InlineMessage
        key="2"
        wrapText
        intent={InlineMessage.Intent.TIP}
        title="Good time to use the market challenge & our response"
        label="When you need to explain the market challenge/problem and our solution"
    />,
    <Columns
        key="3"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <h4>Market Challenge</h4>
                    <p>
                        It&#39;s no secret that the existing ecosystem for getting the right
                        specialty medications to the right patient, at the right time, for the right
                        price, is complex and opaque. Payers and specialty pharmacies are both
                        forced to work from limited knowledge, which makes it nearly impossible to
                        operate effectively. Time and resources are wasted throughout the process,
                        because &#40;for example&#41; payers have little control over making sure
                        that referrals go to the right specialty pharmacy and pharmacies take on
                        unnecessary burdensome administrative tasks.
                    </p>
                    <p>
                        The pressures and challenges only increase as the specialty share of total
                        pharmacy spending has rocketed past 50%.
                    </p>
                </div>
                <div>
                    <h4>Our Response</h4>
                    <p>
                        What if there was a way to fix this system? An elegant, empowering and
                        sustainable solution, created by industry experts, which invites everyone
                        in, facilitates the best match of referral and pharmacy, and enables
                        real-time knowledge gathering and decision-making.
                    </p>
                    <p>
                        A dynamic community that allows specialty pharmacies of all sizes, and
                        payers of all types, to enjoy the benefits that come with greater data
                        transparency, increased process efficiency, an improved member experience
                        and expanded business opportunity. One that widens access and enhances
                        accountability. That balances the costs of care and the value that care
                        provides.
                    </p>
                    <p>
                        A smart and evolving platform that functions effectively for the people
                        it&#39;s really designed to serve: every single patient.
                    </p>
                    <p>
                        That&#39;s the power, the possibility, and the reality of a care-driven
                        marketplace. That&#39;s Free Market Health.
                    </p>
                </div>
            </>
        }
    />,
];

const aboutFMH = [
    <p key="4">
        Outlines three statements that describe what Free Market Health is at the highest level.
    </p>,
    <Columns
        key="5"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <h4>Boilerplate</h4>
                    <div className="margin__top--medium margin__bottom--medium">
                        <InlineMessage
                            wrapText
                            intent={InlineMessage.Intent.TIP}
                            title="Good time to use the boilerplate"
                            label="In a press release"
                        />
                    </div>
                    <p>
                        Founded in 2019 and headquartered in Pittsburgh, Free Market Health partners
                        with forward-thinking payers and specialty pharmacies of all sizes to
                        streamline the complex and opaque specialty medication fulfillment process
                        to ensure patients have timely access to lifesaving and life altering
                        medications. Free Market Health opens up a dynamic, care-driven marketplace
                        that empowers its stakeholders to optimize resources and maximize
                        opportunities with the goal of balancing the cost of care and the value that
                        care provides. Learn more at www.freemarkethealth.com.
                    </p>
                </div>
                <div>
                    <h4>About Free Market Health &#40;Short&#41;</h4>
                    <div className="margin__top--medium margin__bottom--medium">
                        <InlineMessage
                            wrapText
                            intent={InlineMessage.Intent.TIP}
                            title="Good time to use about Free Market Health (short)"
                            label="When you need a short company description for materials"
                        />
                    </div>
                    <p>
                        Founded in 2019 and headquartered in Pittsburgh, PA, Free Market Health
                        supports forward-thinking payers and specialty pharmacies of all sizes who
                        need to operate in a complex and opaque market. We empower all stakeholders
                        to optimize resources and maximize opportunities while focusing on the most
                        important stakeholder of all: the patient.
                    </p>
                </div>
                <div>
                    <h4>Business Description</h4>
                    <div className="margin__top--medium margin__bottom--medium">
                        <InlineMessage
                            wrapText
                            intent={InlineMessage.Intent.TIP}
                            title="Good time to use the business description"
                            label="When you need to explain the “why” behind our solution in a business setting"
                        />
                    </div>
                    <p>
                        Free Market Health is a healthcare technology company dedicated to improving
                        the specialty pharmacy ecosystem with a care-driven marketplace platform.
                    </p>
                    <h4>Why?</h4>
                    <ul>
                        <li>
                            Because as the system stands, misdirected interactions and misaligned
                            incentives can cause delays in medication access of days or even weeks—a
                            burden that wastes resources, generates confusion and ultimately fails
                            patients.
                        </li>
                        <li>
                            Because until now, no model has been able to successfully and seamlessly
                            help match patients to the specialty pharmacy best suited for their care
                            needs—or make value-based care a practical and central part of the
                            process.
                        </li>
                        <li>
                            Because we were founded to fulfill one simple mission: Never let the
                            process fail the patient.
                        </li>
                    </ul>
                </div>
            </>
        }
    />,
];

const elevatorPitch = [
    <p key="6">The elevator pitch is a short statement meant for an industry-savvy audience.</p>,
    <InlineMessage
        key="7"
        wrapText
        intent={InlineMessage.Intent.TIP}
        title="Good time to use the elevator pitch"
        label="When you have less than a minute to describe Free Market Health in a business setting"
    />,
    <p key="8">
        Free Market Health has created a care-driven marketplace platform that improves the
        specialty pharmacy ecosystem. Right now, patients can wait days or even weeks to receive
        medications, and resources are wasted along the way. What we do is seamlessly facilitate a
        match between patients and the specialty pharmacy best suited for their care needs, give
        payers more control of their spending, and enable value-based care. The care-driven
        marketplace empowers specialty pharmacies and payers by creating a better-orchestrated
        process with enhanced visibility and flexibility—and most importantly, it enables better
        patient outcomes.
    </p>,
];

const bbqPitch = [
    <p key="9">The BBQ pitch is a short statement meant for a casual audience.</p>,
    <InlineMessage
        key="10"
        wrapText
        intent={InlineMessage.Intent.TIP}
        title="Good time to use the BBQ pitch:"
        label="When you have less than a minute to describe what Free Market Health does to a friend or acquaintance"
    />,
    <div key="11">
        <p>
            I work at healthcare technology company called Free Market Health. We have a unique
            solution that makes it easier for patients to get the specialty medications they need.
            These aren&#39;t the kind of medications you can walk in and get at your local pharmacy
            like the corner CVS or Walgreens, but medications for complex or rare chronic conditions
            like cancer, rheumatoid arthritis, hemophilia, psoriasis, inflammatory bowel disease and
            hepatitis C.
        </p>
        <p>
            Right now, the system to get those medications is a mess. It&#39;s hard to get a health
            plan to approve the medication. Then once it&#39;s approved, doctors don&#39;t know the
            best pharmacy to fill the prescription. And specialty pharmacies often get prescriptions
            that they can&#39;t fill or don&#39;t get reimbursed properly for them. For these
            reasons and so many more, it can take weeks for a patient to get their medication.
        </p>
        <p>
            To fix this mess, we created a cloud-based platform that streamlines and automates a lot
            of the process, and gives both the pharmacies and the health plans more visibility and
            flexibility. When they use the Free Market Health platform, a prescription can get
            through all the steps it has to take from the doctor to the insurance company to the
            specialty pharmacy quickly and accurately, and get to the patient sooner. There&#39;s
            even a built in feature that&#39;s focused on creating payment incentives for pharmacies
            to provide better overall care for patients.
        </p>
        <p>
            And that&#39;s really what the company is all about: making sure that the process never
            fails the patient.
        </p>
    </div>,
];

export const BrandStory = () => (
    <div>
        <Page
            pageTitle="Brand Story"
            pageDescription="How we speak about our company, brand and products."
            pageContent={
                <>
                    <Section
                        title="Market Challenge & Our Response"
                        contentArray={marketChallenge}
                    />
                    <Section title="About Free Market Health" contentArray={aboutFMH} />
                    <Section title="Elevator Pitch" contentArray={elevatorPitch} />
                    <Section title="BBQ Pitch" contentArray={bbqPitch} />
                </>
            }
        />
    </div>
);
